/**
 * AdminShippingPage
 * ------------------------------------------
 * Admin page to manage shipments:
 *  - Lists all shipping records from the `shipping` table
 *  - Allows searching by order id, name, address, tracking, phone, email
 *  - Lets admin delete an order (and its shipping) or view full order details
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { getApiBase } from "../lib/api";

type ShippingEntry = {
  id: string;
  order_id: string;
  to_name: string;
  to_address: string;
  to_city: string;
  to_state: string;
  to_zip: string;
  to_country: string;
  phone_number?: string;
  email?: string;
  tracking_number: string;
  carrier: string;
  label_url: string;
  status: string;
  created_at: string;
};

export default function AdminShippingPage() {
  const [shipments, setShipments] = useState<ShippingEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const navigate = useNavigate();

  // Load all shipping rows on mount
  useEffect(() => {
    const fetchShipments = async () => {
      const { data, error } = await supabase
        .from("shipping")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching shipments:", error);
      } else {
        setShipments(data || []);
      }

      setLoading(false);
    };

    fetchShipments();
  }, []);

  // Reset pagination when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Delete order + related shipping
  const handleDeleteOrder = async (orderId: string) => {
    const confirmed = window.confirm(
      "Delete this order and all related shipping + items?"
    );
    if (!confirmed) return;

    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();

    if (sessionError || !sessionData.session?.access_token) {
      alert("Please sign in again before deleting orders.");
      return;
    }

    const res = await fetch(`${getApiBase()}/admin/orders/${orderId}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${sessionData.session.access_token}`,
      },
    });

    let body: any = null;

    try {
      body = await res.json();
    } catch {
      body = null;
    }

    if (!res.ok) {
      console.error("Failed to delete order:", body);
      alert(body?.error || "Error deleting order.");
      return;
    }

    setShipments((prev) => prev.filter((s) => s.order_id !== orderId));
  };

  // Search across multiple shipping fields
  const filteredShipments = shipments.filter((s) =>
    `${s.order_id} ${s.to_name} ${s.to_address} ${s.to_city} ${s.to_state} ${s.to_zip} ${s.tracking_number} ${s.phone_number} ${s.email}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  // Simple client-side pagination
  const totalPages = Math.ceil(filteredShipments.length / itemsPerPage);
  const paginatedShipments = filteredShipments.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  if (loading) return <div className="p-4">Loading shipments...</div>;

  return (
    <div className="p-6">
      <h2 className="text-2xl font-semibold mb-4">📦 Shipping Management</h2>

      {/* Search input */}
      <input
        type="text"
        placeholder="Search by confirmation #, name, address, tracking #, phone, email..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="mb-6 p-2 border border-gray-300 rounded w-full focus:outline-none focus:ring-2 focus:ring-[#5c2f2f]"
      />

      {/* Shipping cards */}
      <div className="grid grid-cols-1 gap-4">
        {paginatedShipments.map((s) => (
          <div
            key={s.id}
            className="border border-gray-200 rounded-lg p-4 shadow-sm bg-white"
          >
            <p>
              <strong>Confirmation #:</strong> {s.order_id}
            </p>
            <p>
              <strong>To:</strong> {s.to_name}
            </p>
            <p>
              <strong>Address:</strong> {s.to_address}, {s.to_city},{" "}
              {s.to_state} {s.to_zip}, {s.to_country}
            </p>
            {s.phone_number && (
              <p>
                <strong>Phone:</strong> {s.phone_number}
              </p>
            )}
            {s.email && (
              <p>
                <strong>Email:</strong> {s.email}
              </p>
            )}
            <p>
              <strong>Carrier:</strong> {s.carrier}
            </p>
            <p>
              <strong>Tracking #:</strong> {s.tracking_number || "N/A"}
            </p>
            <p>
              <strong>Status:</strong> {s.status}</p>

            {s.label_url && (
              <a
                href={s.label_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-2 px-4 py-2 bg-[#e8c6c0] text-[#5c2f2f] rounded hover:bg-[#ddb3ab] transition-colors"
              >
                Download Label
              </a>
            )}

            <div className="mt-4 flex gap-2">
              <button
                onClick={() => handleDeleteOrder(s.order_id)}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Delete Order
              </button>

              <button
                onClick={() =>
                  navigate(`/admin/orderManagement/${s.order_id}`)
                }
                className="px-4 py-2 bg-[#a3c9f1] text-[#1e3a5f] rounded hover:bg-[#8bbbe9] transition-colors"
              >
                View Details
              </button>
            </div>
          </div>
        ))}

        {filteredShipments.length === 0 && (
          <p className="text-gray-500">No shipments match your search.</p>
        )}
      </div>

      {/* Pagination buttons */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-6 gap-2 flex-wrap">
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i}
              onClick={() => setCurrentPage(i + 1)}
              className={`px-3 py-1 rounded ${
                currentPage === i + 1
                  ? "bg-[#5c2f2f] text-white"
                  : "bg-gray-200 text-gray-700"
              } hover:bg-[#ddb3ab] transition-colors`}
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
